(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-capa-root]';
  const VARIANT_SELECTOR = '[data-capa-variant]';

  function formatMoney(cents, currency, locale) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';
    try {
      return new Intl.NumberFormat(locale || 'pt-BR', {
        style: 'currency',
        currency: currency || 'BRL',
      }).format(amount / 100);
    } catch (_error) {
      return 'R$ ' + (amount / 100).toFixed(2).replace('.', ',');
    }
  }

  function initCapa(root) {
    if (root.dataset.capaInitialized === 'true') return;

    const variants = Array.from(root.querySelectorAll(VARIANT_SELECTOR));
    const productForm = root.querySelector('product-form');
    const form = productForm && productForm.querySelector('form');
    const variantIdInput = form && form.querySelector('input[name="id"]');
    const addButton = form && form.querySelector('button[name="add"]');
    const quantityInput = root.querySelector('input[name="quantity"]');

    if (!variants.length || !form || !variantIdInput || !addButton) return;

    root.dataset.capaInitialized = 'true';

    const currency = root.dataset.capaCurrency || 'BRL';
    const locale = root.dataset.capaLocale || document.documentElement.lang || 'pt-BR';
    const availability = root.querySelector('[data-capa-availability]');
    const selectedLabel = root.querySelector('[data-capa-selected-label]');
    const mainPrice = root.querySelector('[data-capa-main-price]');
    const colorNote = root.querySelector('[data-capa-color-note]');
    const whatsappLink = root.querySelector('[data-capa-whatsapp]');
    const whatsappNumber = root.dataset.capaWhatsappNumber || '5519981919374';
    const addToCartLabel = root.dataset.capaAddToCartLabel || 'Adicionar ao carrinho';
    const soldOutLabel = root.dataset.capaSoldOutLabel || 'Indisponível';
    const buttonLabel = addButton.querySelector(':scope > span');

    function quantityBounds(variant) {
      const min = Math.max(1, Number.parseInt(variant.dataset.capaQuantityMin || '1', 10) || 1);
      const step = Math.max(1, Number.parseInt(variant.dataset.capaQuantityStep || '1', 10) || 1);
      const maxValue = Number.parseInt(variant.dataset.capaQuantityMax || '', 10);
      const max = Number.isFinite(maxValue) && maxValue >= min ? maxValue : null;

      if (quantityInput) {
        quantityInput.min = String(min);
        quantityInput.dataset.min = String(min);
        quantityInput.step = String(step);
        if (max === null) {
          quantityInput.removeAttribute('max');
          delete quantityInput.dataset.max;
        } else {
          quantityInput.max = String(max);
          quantityInput.dataset.max = String(max);
        }

        const current = Number(quantityInput.value);
        if (!Number.isFinite(current) || current < min) quantityInput.value = String(min);
        if (max !== null && Number(quantityInput.value) > max) quantityInput.value = String(max);
      }

      return { min, max };
    }

    function safeQuantity(bounds) {
      if (!quantityInput) return 1;
      const raw = Number(quantityInput.value);
      let count = Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : bounds.min;
      count = Math.max(bounds.min, count);
      if (bounds.max !== null) count = Math.min(bounds.max, count);
      return count;
    }

    function updateFinalPrices(unitPrice) {
      const bounds = quantityBounds(getSelectedVariant());
      const total = unitPrice * safeQuantity(bounds);
      document.querySelectorAll('[data-capa-final-price]').forEach((node) => {
        node.textContent = formatMoney(total, currency, locale);
      });
    }

    function getSelectedVariant() {
      return variants.find((variant) => variant.checked) || variants[0];
    }

    function syncUrl(variantId) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      } catch (_error) {
        // The form still submits the selected variant when URL updates are unavailable.
      }
    }

    function updateVariant(variant, updateUrl) {
      if (!variant) return;

      const variantId = variant.value;
      const label = variant.dataset.capaLabel || '';
      const unitPrice = Math.max(0, Number(variant.dataset.capaPriceCents || 0));
      const bounds = quantityBounds(variant);
      const quantitySoldOut = variant.dataset.capaQuantitySoldOut === 'true';
      const canAdd = variant.dataset.capaAvailable === 'true' && !quantitySoldOut;

      variants.forEach((choice) => {
        const labelElement = choice.closest('.sc-capa__choice');
        if (labelElement) labelElement.classList.toggle('is-selected', choice === variant);
      });

      variantIdInput.value = variantId;
      variantIdInput.disabled = !canAdd;
      variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));

      addButton.disabled = !canAdd;
      if (buttonLabel) buttonLabel.textContent = canAdd ? addToCartLabel : soldOutLabel;

      if (selectedLabel) selectedLabel.textContent = label;
      if (mainPrice) mainPrice.textContent = formatMoney(unitPrice, currency, locale);

      root.querySelectorAll('[data-capa-option-price]').forEach((priceNode) => {
        const option = variants.find((choice) => choice.value === priceNode.dataset.capaOptionPrice);
        if (!option) return;
        const optionPrice = Math.max(0, Number(option.dataset.capaPriceCents || 0));
        priceNode.textContent = formatMoney(optionPrice, currency, locale);
      });

      if (availability) {
        availability.textContent = canAdd
          ? 'Disponível para adicionar ao carrinho.'
          : 'Esta opção está indisponível no momento.';
      }

      if (colorNote) {
        const choiceLabel = variant.closest('.sc-capa__choice');
        colorNote.hidden = !choiceLabel || choiceLabel.dataset.capaColor !== 'colorida';
      }

      if (whatsappLink) {
        const message =
          'Olá! Estou vendo a capa para galão de 20 litros personalizada e gostaria de tirar uma dúvida sobre a minha logo. Base: ' +
          label;
        whatsappLink.href =
          'https://wa.me/' + whatsappNumber + '?text=' + encodeURIComponent(message);
      }

      updateFinalPrices(unitPrice);
      root.dataset.capaSelectedVariant = variantId;
      if (updateUrl) syncUrl(variantId);
    }

    root.addEventListener('change', (event) => {
      if (event.target.matches(VARIANT_SELECTOR)) {
        updateVariant(event.target, true);
        return;
      }

      if (event.target === quantityInput) {
        const selected = getSelectedVariant();
        const bounds = quantityBounds(selected);
        if (quantityInput) quantityInput.value = String(safeQuantity(bounds));
        updateFinalPrices(Math.max(0, Number(selected.dataset.capaPriceCents || 0)));
      }
    });

    root.addEventListener('input', (event) => {
      if (event.target !== quantityInput) return;
      const selected = getSelectedVariant();
      updateFinalPrices(Math.max(0, Number(selected.dataset.capaPriceCents || 0)));
    });

    const requestedVariantId = new URL(window.location.href).searchParams.get('variant');
    const initial =
      variants.find((variant) => variant.value === requestedVariantId) ||
      variants.find((variant) => variant.value === root.dataset.capaInitialVariant) ||
      getSelectedVariant();

    initial.checked = true;
    updateVariant(initial, false);
  }

  function boot(target) {
    if (!target) return;
    if (target.matches && target.matches(ROOT_SELECTOR)) initCapa(target);
    if (target.querySelectorAll) {
      target.querySelectorAll(ROOT_SELECTOR).forEach(initCapa);
    }
  }

  boot(document);
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
